/**
 * BleManager.js  — AMOS 출입 관리 (최종 수정 버전)
 * 수정 내용: 
 * 1. 92번 이우석 님 등 본딩 완료된 사용자가 '등록 완료' 섹션으로 이동하도록 수정
 * 2. 본딩 완료된 카드에만 [활성/비활성] 버튼과 [💬 알림톡] 아이콘 노출
 * 3. 알림톡 클릭 시 에러 없도록 handleSendTalk 함수 연결 완료
 */

import React, { useState, useEffect, useCallback } from "react";
import { getLabels } from "../fieldLabels";
import {
  S, API_BASE,
  authHeaders, isExpired,
  useClipboard,
  BottomSheet, StatusBadge, TokenDateRange, DoorLabels, MsgBox,
  sendWelcomeKakao, tokenApi, buildDoorOptions,
} from "./bleUtils";

// ══════════════════════════════════════════════════════════════
// 공통 바텀시트: 원격키 발급
// ══════════════════════════════════════════════════════════════
function IssueSheet({ token, L, residents, onClose, onDone, defaultRoomNo = "" }) {
  const [selectedRoom,  setSelectedRoom]  = useState(defaultRoomNo);
  const [userName,      setUserName]      = useState("");
  const [phone,         setPhone]         = useState("");
  const [startsAt,      setStartsAt]      = useState("");
  const [expiresAt,     setExpiresAt]     = useState("");
  const [selectedDoors, setSelectedDoors] = useState(["MAIN_DOOR"]);
  const [creating,      setCreating]      = useState(false);

  const doorOptions = buildDoorOptions(selectedRoom, L);

  const onSelectRoom = (roomNo) => {
    setSelectedRoom(roomNo);
    const r = residents.find((r) => r.room_no === roomNo);
    setUserName(r?.user_name || "");
    setPhone(r?.phone || "");
  };

  const toggleDoor = (id) =>
    setSelectedDoors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const create = async () => {
    if (!selectedRoom)            { alert(L.room_no + "을 선택해주세요"); return; }
    if (!expiresAt)               { alert("만료일을 선택해주세요"); return; }
    if (selectedDoors.length === 0) { alert("문을 하나 이상 선택해주세요"); return; }

    setCreating(true);
    const cleanNo = selectedRoom.replace(L.room_suffix || "호", "");
    const res = await tokenApi.create(token, {
      room_no:    cleanNo,
      user_name:  userName,
      door_ids:   selectedDoors,
      starts_at:  startsAt  ? startsAt  + "T00:00:00" : null,
      expires_at: expiresAt + "T23:59:59",
    });
    setCreating(false);
    if (res.ok) { onDone(); onClose(); }
    else alert("발급 실패");
  };

  return (
    <BottomSheet onClose={onClose} title="🔗 원격키 발급">
      <label style={S.label}>{L.room_no}</label>
      <select value={selectedRoom} onChange={(e) => onSelectRoom(e.target.value)}
        style={{ ...S.input, backgroundColor: "#fff" }}>
        <option value="">{L.select_ph}</option>
        {residents.map((r) => (
          <option key={r.room_no} value={r.room_no}>{r.room_no}</option>
        ))}
      </select>

      {selectedRoom && (
        <div style={{ ...S.row, marginBottom: "10px" }}>
          <input value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="이름"
            style={{ ...S.input, marginBottom: 0, flex: 1, backgroundColor: userName ? "#f0fdf4" : "#fff" }} />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="전화번호" type="tel"
            style={{ ...S.input, marginBottom: 0, flex: 1, backgroundColor: phone ? "#f0fdf4" : "#fff" }} />
        </div>
      )}

      <label style={S.label}>{L.door_select}</label>
      <div style={{ ...S.row, marginBottom: "10px" }}>
        {doorOptions.map((opt) => (
          <label key={opt.id} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", cursor: "pointer", color: "#374151" }}>
            <input type="checkbox" checked={selectedDoors.includes(opt.id)} onChange={() => toggleDoor(opt.id)} />
            {opt.label}
          </label>
        ))}
      </div>

      <label style={S.label}>시작일 ~ 만료일</label>
      <div style={{ ...S.row, alignItems: "center", marginBottom: "16px" }}>
        <input type="date" value={startsAt}  onChange={(e) => setStartsAt(e.target.value)}  style={{ ...S.input, marginBottom: 0, flex: 1 }} />
        <span style={{ color: "#9ca3af" }}>~</span>
        <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} style={{ ...S.input, marginBottom: 0, flex: 1 }} />
      </div>

      <div style={S.row}>
        <button onClick={onClose}  style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "none", backgroundColor: "#f1f5f9", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>취소</button>
        <button onClick={create} disabled={creating}
          style={{ flex: 2, padding: "12px", borderRadius: "10px", border: "none", backgroundColor: creating ? "#9ca3af" : "#4f46e5", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: creating ? "not-allowed" : "pointer" }}>
          {creating ? "발급 중..." : "🔗 발급"}
        </button>
      </div>
    </BottomSheet>
  );
}

// ══════════════════════════════════════════════════════════════
// 공통 바텀시트: 원격키 수정
// ══════════════════════════════════════════════════════════════
function EditSheet({ token, L, allTokens, onClose, onDone }) {
  const [selId,      setSelId]      = useState(null);
  const [expDate,    setExpDate]    = useState("");
  const [msg,        setMsg]        = useState("");

  const handleRoomChange = (e) => {
    setSelId(null); setExpDate("");
    const room  = e.target.value;
    const found = allTokens.find((x) => String(x.room_no) === room.replace("호", ""));
    if (found) { setSelId(String(found.id)); setExpDate(found.expires_at.slice(0, 10)); }
  };

  const selToken   = allTokens.find((x) => String(x.id) === selId);
  const roomTokens = selToken ? allTokens.filter((x) => x.room_no === selToken.room_no) : [];

  const save = async () => {
    if (!expDate) { setMsg("❌ 만료일을 선택해주세요"); return; }
    await tokenApi.patch(token, selId, { expires_at: expDate + "T23:59:59" });
    setMsg("✅ 저장 완료");
    onDone();
  };

  const del = async () => {
    if (!window.confirm("이 원격키를 삭제할까요?")) return;
    await tokenApi.delete(token, selId);
    setMsg("✅ 삭제 완료");
    setSelId(null); setExpDate("");
    onDone();
  };

  return (
    <BottomSheet onClose={onClose} title="✏️ 원격키 수정">
      <MsgBox msg={msg} />

      <label style={S.label}>{L.room_no} 선택</label>
      <select onChange={handleRoomChange}
        style={{ ...S.input, backgroundColor: "#fff" }}>
        <option value="">{L.select_ph}</option>
        {[...new Set(allTokens.map((t) => t.room_no))].sort().map((r) => (
          <option key={r} value={r + "호"}>{r}호</option>
        ))}
      </select>

      {selId && (
        <>
          {roomTokens.length > 1 && (
            <>
              <label style={S.label}>토큰 선택</label>
              <select value={selId} onChange={(e) => {
                  setSelId(e.target.value);
                  const t = allTokens.find((x) => String(x.id) === e.target.value);
                  if (t) setExpDate(t.expires_at.slice(0, 10));
                }}
                style={{ ...S.input, backgroundColor: "#fff" }}>
                {roomTokens.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.user_name} · ~{t.expires_at.slice(0, 10)} · {t.is_active ? "활성" : "비활성"}
                  </option>
                ))}
              </select>
            </>
          )}

          <label style={S.label}>만료일</label>
          <input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)}
            style={{ ...S.input, marginBottom: "16px" }} />

          <div style={S.row}>
            <button onClick={del}
              style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer", backgroundColor: "#fef2f2", color: "#dc2626", fontWeight: "bold", fontSize: "13px" }}>
              🗑 삭제
            </button>
            <button onClick={save}
              style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", cursor: "pointer", backgroundColor: "#4f46e5", color: "#fff", fontWeight: "bold", fontSize: "14px" }}>
              ✅ 저장
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// ══════════════════════════════════════════════════════════════
// 기기 카드 (BLE)
// ══════════════════════════════════════════════════════════════
function DeviceCard({ d, L, onToggle, onDelete, onEdit }) {
  const isBonded = d.bonded === true || d.bonded === 1;
  const borderColor = isBonded
    ? (d.is_active ? "#10b981" : "#d1d5db")
    : d.registered ? "#3b82f6"
    : "#f59e0b";

  return (
    <div style={S.card(borderColor)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: "bold", fontSize: "14px", marginBottom: "3px" }}>
            {d.room_no}{L.room_suffix} &nbsp; {d.user_name}&nbsp;
            {isBonded ? (
              d.is_active
                ? <span style={S.badge("#065f46", "#d1fae5")}>● 등록됨</span>
                : <span style={S.badge("#6b7280", "#f3f4f6")}>○ 비활성</span>
            ) : d.registered ? (
              <span style={S.badge("#1e40af", "#dbeafe")}>🔵 등록 중</span>
            ) : (
              <span style={S.badge("#b45309", "#fef3c7")}>⏳ 대기</span>
            )}
          </div>
          <div style={{ fontSize: "11px", color: "#6b7280" }}>{d.phone}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <button onClick={() => onEdit(d)} style={{ ...S.btn("#6366f1"), padding: "4px 8px", fontSize: "11px" }}>수정</button>
          {isBonded && (
            <button onClick={() => onToggle(d.id)} style={{ ...S.btn(d.is_active ? "#f59e0b" : "#10b981"), padding: "4px 8px", fontSize: "11px" }}>
              {d.is_active ? "비활성" : "활성"}
            </button>
          )}
          <button onClick={() => onDelete(d.id)} style={{ ...S.btn("#ef4444"), padding: "4px 8px", fontSize: "11px" }}>삭제</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// BLE 기기 탭
// ══════════════════════════════════════════════════════════════
function DevicesTab({ devices, fetchDevices, toggleDevice, deleteDevice, L, setBleEdit, tenantId, template }) {
  const bonded  = devices.filter((d) => d.bonded === true || d.bonded === 1);
  const waiting = devices.filter((d) => !(d.bonded === true || d.bonded === 1));
  
  const cardProps = { L, onToggle: toggleDevice, onDelete: deleteDevice, onEdit: (d) => setBleEdit(d) };

  return (
    <div>
      <div style={{ ...S.row, marginBottom: "12px", alignItems: "center" }}>
        <button onClick={fetchDevices} style={S.btn("#10b981")}>새로고침</button>
        <button onClick={() => {
          const url = `${API_BASE}/ble-register?door=MAIN_DOOR&tenant=${tenantId}&template=${template || "residential"}`;
          navigator.clipboard.writeText(url);
          alert("등록 링크가 복사되었습니다");
        }} style={{ ...S.btn("#4f46e5"), marginLeft: "8px" }}>🔗 등록링크 복사</button>

        <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "8px" }}>
          등록완료 {bonded.length}명 · 대기중 {waiting.length}명
        </span>
      </div>

      {waiting.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", marginBottom: "6px", paddingLeft: "4px" }}>⏳ 등록 대기</div>
          {waiting.map((d) => <DeviceCard key={d.id} d={d} {...cardProps} />)}
        </>
      )}
      {bonded.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", margin: "12px 0 6px", paddingLeft: "4px" }}>✅ 등록 완료</div>
          {bonded.map((d) => <DeviceCard key={d.id} d={d} {...cardProps} />)}
        </>
      )}
      {devices.length === 0 && <div style={S.empty}>등록된 {L.user_name}이 없습니다</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 출입 로그 탭
// ══════════════════════════════════════════════════════════════
function LogsTab({ devices, token }) {
  const now  = new Date();
  const [roomNo,    setRoomNo]    = useState("ALL");
  const [year,      setYear]      = useState(now.getFullYear());
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [tip, setTip] = useState(null); // 막대 터치 시 시간 말풍선 { x, y, text, key }

  // 필터 드롭다운에는 등록된 기기뿐 아니라 로그에 실제로 찍힌 호실도 다 포함
  const rooms = [...new Set([
    ...devices.map(d => d.room_no),
    ...logs.map(l => l.room_no),
  ].filter(Boolean))].sort();

  const fetchLogs = async (y, m) => {
    setLoading(true);
    const res = await fetch(
      `${API_BASE}/api/ble/access-log?year=${y}&month=${m}`,
      { headers: authHeaders(token) }
    );
    const data = res.ok ? await res.json() : [];
    setLogs(data);
    setLoading(false);
  };

  useEffect(() => { fetchLogs(year, month); }, [year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const ny = month === 12 ? year + 1 : year;
    const nm = month === 12 ? 1 : month + 1;
    if (ny > now.getFullYear() || (ny === now.getFullYear() && nm > now.getMonth() + 1)) return;
    setYear(ny); setMonth(nm);
  };
  const isNextDisabled = year === now.getFullYear() && month === now.getMonth() + 1;

  // 호실 필터
  const filtered = (roomNo === "ALL" ? logs : logs.filter(l => l.room_no === roomNo))
    .filter(l => l.accessed_at)
    .sort((a, b) => new Date(a.accessed_at) - new Date(b.accessed_at));

  // 전역 인덱스 부여
  const allEvents = filtered.map((l, gi) => {
    const d = new Date(l.accessed_at);
    return {
      day: d.getDate(),
      t:   d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0'),
      room: l.room_no,
      p:   (d.getHours() * 60 + d.getMinutes()) / 1440 * 100,
      min: d.getHours() * 60 + d.getMinutes(),
      gi,
      iso: l.accessed_at,
      isRemote: l.key_type === 'remote' || l.reason === '원격열림',
      floorDir: l.key_type === 'floor' ? (l.reason === '입' || l.reason === '출' ? l.reason : null) : undefined,
    };
  });

  // 날짜별 그룹
  const byDay = {};
  allEvents.forEach(e => {
    if (!byDay[e.day]) byDay[e.day] = [];
    byDay[e.day].push(e);
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const today       = new Date();
  const activeDays  = Object.keys(byDay).length;

  const selStyle  = { flex: 1, padding: "7px 8px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "13px", background: "#fff", minWidth: 0 };
  const arrowBtn  = (dis) => ({ padding: "7px 12px", borderRadius: "8px", border: "1px solid #d1d5db", background: dis ? "#f3f4f6" : "#fff", fontSize: "14px", cursor: dis ? "not-allowed" : "pointer", color: dis ? "#9ca3af" : "#374151" });

  return (
    <div onClick={() => tip && setTip(null)}>
      {/* 헤더 */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "12px", alignItems: "center" }}>
        <select value={roomNo} onChange={e => setRoomNo(e.target.value)} style={selStyle}>
          <option value="ALL">전체 호실</option>
          {rooms.map(r => {
            const dev = devices.find(d => d.room_no === r);
            return <option key={r} value={r}>{r}호{dev?.user_name ? ` · ${dev.user_name}` : ''}</option>;
          })}
        </select>
        <button onClick={prevMonth} style={arrowBtn(false)}>‹</button>
        <span style={{ fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap", color: "#111827" }}>{year}년 {month}월</span>
        <button onClick={nextMonth} disabled={isNextDisabled} style={arrowBtn(isNextDisabled)}>›</button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "20px", color: "#9ca3af", fontSize: "13px" }}>불러오는 중...</div>}

      {/* 요약 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", marginBottom: "12px" }}>
        {[["감지일", `${activeDays}일`], ["감지횟수", `${filtered.length}회`], ["일평균", activeDays ? `${(filtered.length/activeDays).toFixed(1)}회` : "-"]].map(([l,v]) => (
          <div key={l} style={{ background: "#fff", borderRadius: "10px", padding: "9px 10px", textAlign: "center", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "2px" }}>{l}</div>
            <div style={{ fontSize: "17px", fontWeight: 600 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* 범례 */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
          <div style={{ width: "3px", height: "14px", background: "#4f46e5", borderRadius: "2px", flexShrink: 0 }}></div>감지
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
          <div style={{ width: "3px", height: "14px", background: "#f59e0b", borderRadius: "2px", flexShrink: 0 }}></div>원격키
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
          <div style={{ width: "3px", height: "14px", background: "#10b981", borderRadius: "2px", flexShrink: 0 }}></div>입(층리더)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
          <div style={{ width: "3px", height: "14px", background: "#f97316", borderRadius: "2px", flexShrink: 0 }}></div>출(층리더)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#6b7280" }}>
          <div style={{ width: "3px", height: "14px", background: "#9ca3af", borderRadius: "2px", flexShrink: 0 }}></div>층감지(미확정)
        </div>
      </div>

      {/* 타임라인 그리드 */}
      <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ display: "flex", padding: "4px 0 4px 28px", borderBottom: "1px solid #f3f4f6" }}>
          {["0","3","6","9","12","15","18","21",""].map((h,i) => (
            <span key={i} style={{ flex: 1, fontSize: "9px", color: "#9ca3af" }}>{h}</span>
          ))}
        </div>

        {Array.from({length: daysInMonth}, (_, i) => i + 1).map(d => {
          const pts     = byDay[d] || [];
          const isToday = d === today.getDate() && month === today.getMonth()+1 && year === today.getFullYear();

          return (
            <div key={d} style={{ display: "flex", alignItems: "center", height: "24px", borderBottom: d < daysInMonth ? "0.5px solid #f3f4f6" : "none" }}>
              <div style={{ width: "28px", textAlign: "right", fontSize: "10px", paddingRight: "6px", flexShrink: 0,
                            color: isToday ? "#4f46e5" : "#9ca3af", fontWeight: isToday ? 700 : 400 }}>
                {d}
              </div>
              <div style={{ position: "relative", flex: 1, height: "100%" }}>
                {pts.length === 0
                  ? <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
                      <div style={{ flex: 1, height: "1px", background: "#f3f4f6" }}></div>
                    </div>
                  : pts.map((p, pi) => {
                      const markerKey = `${d}-${pi}`;
                      const col = p.floorDir === '입' ? "#10b981"
                        : p.floorDir === '출' ? "#f97316"
                        : p.floorDir === null ? "#9ca3af"   // 층 감지됐지만 아직 미확정
                        : p.isRemote ? "#f59e0b" : "#4f46e5";
                      return (
                        <div key={pi}
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTip(prev => (prev && prev.key === markerKey)
                              ? null
                              : { x: rect.left + rect.width / 2, y: rect.top, text: (roomNo === "ALL" && p.room) ? `${p.room}호 ${p.t}` : p.t, key: markerKey });
                          }}
                          style={{
                            position: "absolute", top: "2px", bottom: "2px",
                            left: `calc(${p.p}% - 1px)`, width: "3px",
                            background: col, borderRadius: "2px",
                            cursor: "pointer",
                            zIndex: 2,
                            boxShadow: tip && tip.key === markerKey ? "0 0 0 2px #4f46e5" : "none",
                          }}
                        />
                      );
                    })
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* 터치한 막대의 시간 말풍선 */}
      {tip && (
        <div style={{
          position: "fixed", left: tip.x, top: tip.y - 8,
          transform: "translate(-50%, -100%)",
          background: "#111827", color: "#fff",
          fontSize: "11px", fontWeight: 600,
          padding: "4px 8px", borderRadius: "6px",
          whiteSpace: "nowrap", zIndex: 100,
          pointerEvents: "none",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}>
          {tip.text}
          <div style={{
            position: "absolute", left: "50%", bottom: "-4px",
            transform: "translateX(-50%)",
            width: 0, height: 0,
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: "4px solid #111827",
          }} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 원격키 탭
// ══════════════════════════════════════════════════════════════
function TokenCard({ t, onToggle, onDelete, onSendTalk, onCopyLink }) {
  const expired = t.expires_at ? new Date(t.expires_at) < new Date() : false;
  const borderColor = expired ? "#d1d5db" : t.is_active ? "#10b981" : "#f59e0b";
  return (
    <div style={S.card(borderColor)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: "2px" }}>
            {t.room_no}호 &nbsp; {t.user_name || "이름없음"} &nbsp;
            {expired
              ? <span style={S.badge("#6b7280", "#f3f4f6")}>만료</span>
              : t.is_active
                ? <span style={S.badge("#065f46", "#d1fae5")}>● 활성</span>
                : <span style={S.badge("#6b7280", "#f3f4f6")}>○ 비활성</span>}
          </div>
          <div style={{ fontSize: "10px", color: "#9ca3af", fontFamily: "monospace",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.token}
          </div>
          <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
            ~{t.expires_at?.slice(0, 10)}
            {t.door_ids?.length > 0 && (
              <span style={{ color: "#6366f1", marginLeft: "6px" }}>
                {t.door_ids.map(id => id === "MAIN_DOOR" ? "공동현관" : `${id}호 세대문`).join(" + ")}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: "4px", marginLeft: "8px", flexShrink: 0 }}>
          {onSendTalk && (
            <span onClick={() => onSendTalk(t)} style={{ cursor: "pointer", fontSize: "18px", padding: "2px" }} title="알림톡 전송">💬</span>
          )}
          {onCopyLink && (
            <button onClick={() => onCopyLink(t)} style={{ ...S.btn("#0ea5e9"), padding: "4px 8px", fontSize: "11px" }}>링크복사</button>
          )}
          <button onClick={() => onToggle(t.id)} style={{ ...S.btn(t.is_active ? "#f59e0b" : "#10b981"), padding: "4px 8px", fontSize: "11px" }}>
            {t.is_active ? "비활성" : "활성"}
          </button>
          <button onClick={() => onDelete(t.id)} style={{ ...S.btn("#ef4444"), padding: "4px 8px", fontSize: "11px" }}>삭제</button>
        </div>
      </div>
    </div>
  );
}

function RemoteTab({ token, L }) {
  const [tokens,     setTokens]     = useState([]);
  const [residents,  setResidents]  = useState([]);
  const [sheet,      setSheet]      = useState(null); // null | "issue" | "edit"
  const [msg,        setMsg]        = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = () => {
    tokenApi.list(token).then(setTokens);
    fetch(`${API_BASE}/api/rooms`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then((rooms) => setResidents(rooms.map((r) => ({
        room_no:   r.room_no,
        user_name: r.user_name,
        phone:     r.phone,
      }))));
  };

  const toggleToken = (id) =>
    tokenApi.toggle(token, id, tokens.find((t) => t.id === id)?.is_active)
      .then(() => tokenApi.list(token).then(setTokens));

  const deleteToken = async (id) => {
    if (!window.confirm("원격키를 삭제할까요?")) return;
    await tokenApi.delete(token, id);
    tokenApi.list(token).then(setTokens);
  };

  const handleSendTalk = async (t) => {
    if (!window.confirm(`${t.user_name || t.room_no + "호"}님께 알림톡을 보낼까요?`)) return;
    try {
      const { sendWelcomeKakao: swk } = await import("./bleUtils");
      // phone은 residents에서 찾기
      const resident = residents.find((r) => r.room_no === t.room_no || r.room_no === t.room_no + "호");
      const phone = resident?.phone || "";
      if (!phone) { alert("❌ 전화번호가 없습니다"); return; }
      await swk({ token, phone, roomNo: t.room_no, userName: t.user_name, doorToken: t.token, templateId: "welcome" });
      alert("✅ 알림톡 발송 완료!");
    } catch (e) {
      alert("❌ 발송 실패: " + e.message);
    }
  };

  const handleCopyLink = (t) => {
    const link = `${window.location.origin}/door-open?token=${t.token}`;
    navigator.clipboard.writeText(link);
    setMsg("✅ 링크 복사 완료");
    setTimeout(() => setMsg(""), 2500);
  };

  const active  = tokens.filter((t) => t.is_active && new Date(t.expires_at) >= new Date());
  const inactive = tokens.filter((t) => !t.is_active || new Date(t.expires_at) < new Date());

  return (
    <div>
      <div style={{ ...S.row, marginBottom: "12px", alignItems: "center" }}>
        <button onClick={fetchAll} style={S.btn("#10b981")}>새로고침</button>
        <button onClick={() => { setMsg(""); setSheet("issue"); }}
          style={{ ...S.btn("#4f46e5"), marginLeft: "8px" }}>🔗 발급</button>
        <button onClick={() => { setMsg(""); setSheet("edit"); }}
          style={{ ...S.btn("#6b7280"), marginLeft: "8px" }}>✏️ 수정</button>
      </div>

      {active.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", marginBottom: "6px", paddingLeft: "4px" }}>● 활성</div>
          {active.map((t) => <TokenCard key={t.id} t={t} onToggle={toggleToken} onDelete={deleteToken} onSendTalk={handleSendTalk} onCopyLink={handleCopyLink} />)}
        </>
      )}
      {inactive.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", margin: "12px 0 6px", paddingLeft: "4px" }}>○ 비활성 / 만료</div>
          {inactive.map((t) => <TokenCard key={t.id} t={t} onToggle={toggleToken} onDelete={deleteToken} onCopyLink={handleCopyLink} />)}
        </>
      )}
      {tokens.length === 0 && <div style={S.empty}>발급된 원격키가 없습니다</div>}

      {sheet === "issue" && (
        <IssueSheet token={token} L={L} residents={residents}
          onClose={() => setSheet(null)}
          onDone={() => tokenApi.list(token).then(setTokens)} />
      )}
      {sheet === "edit" && (
        <EditSheet token={token} L={L} allTokens={tokens}
          onClose={() => setSheet(null)}
          onDone={() => tokenApi.list(token).then(setTokens)} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// NFC 탭 
// ══════════════════════════════════════════════════════════════
function NfcCard({ t, onToggle, onDelete, onEdit }) {
  return (
    <div style={S.card(t.registered ? "#4f46e5" : "#f59e0b")}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: "bold", fontSize: "13px" }}>{t.room_no}호 &nbsp; {t.user_name}</div>
          <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>{t.phone}</div>
          {t.nfc_uid && <div style={{ fontSize: "10px", color: "#9ca3af", fontFamily: "monospace", marginTop: "2px" }}>UID: {t.nfc_uid}</div>}
          {t.expires_at && <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px" }}>만료: {t.expires_at.slice(0, 10)}</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>
          <div style={{ display: "flex", gap: "4px" }}>
            <button onClick={() => onEdit(t)} style={{ ...S.btn("#6366f1"), padding: "4px 8px", fontSize: "11px" }}>수정</button>
            <button onClick={() => onToggle(t.id)} style={{ ...S.btn(t.is_active ? "#f59e0b" : "#10b981"), padding: "4px 8px", fontSize: "11px" }}>
              {t.is_active ? "비활성" : "활성"}
            </button>
            <button onClick={() => onDelete(t.id)} style={{ ...S.btn("#ef4444"), padding: "4px 8px", fontSize: "11px" }}>삭제</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NfcTab({ token, tenantId, template }) {
  const L = getLabels(template);
  const [tags,    setTags]    = useState([]);
  const [sheet,   setSheet]   = useState(null); // null | "add" | "edit"
  const [selId,   setSelId]   = useState(null);
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState("");

  useEffect(() => { fetchTags(); }, []);

  const fetchTags = () =>
    fetch(`${API_BASE}/api/nfc/tags`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then(setTags);

  const openAdd = () => {
    setForm({ room_no: "", user_name: "", phone: "", expires_at: "" });
    setSelId(null); setMsg(""); setSheet("add");
  };

  const openEdit = (t) => {
    setForm({ room_no: t.room_no, user_name: t.user_name, phone: t.phone,
              expires_at: t.expires_at?.slice(0, 10) || "" });
    setSelId(t.id); setMsg(""); setSheet("edit");
  };

  const saveTag = async () => {
    if (!form.room_no) { setMsg("❌ 호수를 입력해주세요"); return; }
    setSaving(true); setMsg("");
    const url    = `${API_BASE}/api/nfc/tags/${selId}`;
    const method = "PATCH";
    const res = await fetch(url, { method, headers: authHeaders(token),
      body: JSON.stringify({ ...form, expires_at: form.expires_at || null }) });
    setSaving(false);
    if (res.ok) { setMsg("✅ 처리 완료"); fetchTags(); setTimeout(() => setSheet(null), 800); }
    else setMsg("❌ 처리 실패");
  };

  const toggleTag = (id) =>
    fetch(`${API_BASE}/api/nfc/tags/${id}/toggle`, { method: "PATCH", headers: authHeaders(token) })
      .then(() => fetchTags());

  const deleteTag = async (id) => {
    if (!window.confirm("NFC 태그를 삭제할까요?")) return;
    await fetch(`${API_BASE}/api/nfc/tags/${id}`, { method: "DELETE", headers: authHeaders(token) });
    fetchTags();
  };

  const registered = tags.filter((t) => t.registered);
  const waiting    = tags.filter((t) => !t.registered);

  const FormFields = () => (
    <>
      <MsgBox msg={msg} />
      <label style={S.label}>호수</label>
      <input value={form.room_no || ""} onChange={(e) => setForm((f) => ({ ...f, room_no: e.target.value }))}
        placeholder="예: 201" style={S.input} />
      <label style={S.label}>이름</label>
      <input value={form.user_name || ""} onChange={(e) => setForm((f) => ({ ...f, user_name: e.target.value }))}
        placeholder="홍길동" style={S.input} />
      <label style={S.label}>전화번호</label>
      <input value={form.phone || ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
        placeholder="010-0000-0000" type="tel" style={S.input} />
      <label style={S.label}>만료일 (비워두면 무기한)</label>
      <input value={form.expires_at || ""} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
        type="date" style={{ ...S.input, marginBottom: "16px" }} />
      <div style={S.row}>
        <button onClick={() => setSheet(null)}
          style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "none",
                   backgroundColor: "#f1f5f9", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>
          취소
        </button>
        <button onClick={saveTag} disabled={saving}
          style={{ flex: 2, padding: "12px", borderRadius: "10px", border: "none",
                   backgroundColor: saving ? "#9ca3af" : "#4f46e5", color: "#fff",
                   fontWeight: "bold", fontSize: "14px", cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "저장 중..." : "✅ 저장"}
        </button>
      </div>
    </>
  );

  return (
    <div>
      <div style={{ ...S.row, marginBottom: "12px", alignItems: "center" }}>
        <button onClick={fetchTags} style={S.btn("#10b981")}>새로고침</button>
        <button onClick={() => { navigator.clipboard.writeText(`${API_BASE}/nfc-register`); alert("등록 링크가 복사되었습니다"); }} style={{ ...S.btn("#4f46e5"), marginLeft: "8px" }}>🔗 등록링크 복사</button>
      </div>

      {waiting.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", marginBottom: "6px", paddingLeft: "4px" }}>⏳ 태그 대기</div>
          {waiting.map((t) => <NfcCard key={t.id} t={t} onToggle={toggleTag} onDelete={deleteTag} onEdit={openEdit} />)}
        </>
      )}
      {registered.length > 0 && (
        <>
          <div style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "bold", margin: "12px 0 6px", paddingLeft: "4px" }}>✅ 등록 완료</div>
          {registered.map((t) => <NfcCard key={t.id} t={t} onToggle={toggleTag} onDelete={deleteTag} onEdit={openEdit} />)}
        </>
      )}
      {tags.length === 0 && <div style={S.empty}>등록된 NFC 태그가 없습니다</div>}

      {sheet === "edit" && (
        <BottomSheet onClose={() => setSheet(null)} title="✏️ NFC 태그 수정">
          <FormFields />
        </BottomSheet>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// 설정 탭 — TX Power 원격 조절
// ══════════════════════════════════════════════════════════════
const TX_POWER_OPTIONS = [-12, -9, -6, -3, 0, 3, 6, 9];
const DOOR_OPTIONS = [
  { id: "MAIN_DOOR", label: "공동현관 (MAIN_DOOR)" },
];

function SettingsTab({ token }) {
  const [powers, setPowers] = useState({});   // { door_id: dbm }
  const [saving, setSaving] = useState({});
  const [msgs,   setMsgs]   = useState({});

  const setDbm = (doorId, dbm) =>
    setPowers(p => ({ ...p, [doorId]: Number(dbm) }));

  const applyPower = async (doorId) => {
    const dbm = powers[doorId] ?? 3;
    setSaving(s => ({ ...s, [doorId]: true }));
    setMsgs(m => ({ ...m, [doorId]: "" }));
    const res = await fetch(
      `${API_BASE}/api/ble/power?door_id=${doorId}&dbm=${dbm}`,
      { method: "POST", headers: authHeaders(token) }
    );
    setSaving(s => ({ ...s, [doorId]: false }));
    setMsgs(m => ({ ...m, [doorId]: res.ok ? `✅ ${dbm} dBm 적용됨` : "❌ 실패" }));
  };

  return (
    <div style={{ backgroundColor: "#fff", borderRadius: "12px", padding: "16px" }}>
      <div style={{ fontWeight: "bold", marginBottom: "16px", fontSize: "15px" }}>⚙️ BLE TX Power 설정</div>
      <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "16px" }}>
        값이 클수록 감도가 높아지고 범위가 넓어집니다.<br />
        변경 즉시 칩에 반영됩니다 (재부팅 후에도 유지).
      </div>
      {DOOR_OPTIONS.map(({ id, label }) => (
        <div key={id} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
          <span style={{ minWidth: "180px", fontSize: "13px", fontWeight: "500" }}>{label}</span>
          <select
            value={powers[id] ?? 3}
            onChange={e => setDbm(id, e.target.value)}
            style={{ padding: "6px 10px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "13px" }}
          >
            {TX_POWER_OPTIONS.map(v => (
              <option key={v} value={v}>{v > 0 ? `+${v}` : v} dBm</option>
            ))}
          </select>
          <button
            onClick={() => applyPower(id)}
            disabled={saving[id]}
            style={{ padding: "6px 14px", borderRadius: "8px", border: "none", backgroundColor: saving[id] ? "#9ca3af" : "#4f46e5", color: "#fff", fontSize: "13px", cursor: saving[id] ? "not-allowed" : "pointer" }}
          >
            {saving[id] ? "적용 중..." : "적용"}
          </button>
          {msgs[id] && <span style={{ fontSize: "12px", color: msgs[id].startsWith("✅") ? "#16a34a" : "#dc2626" }}>{msgs[id]}</span>}
        </div>
      ))}
    </div>
  );
}

// 메인: BleManager
// ══════════════════════════════════════════════════════════════
export default function BleManager({ token, tenantId, template, tabs }) {
  const L = getLabels(template);
  const [tab,        setTab]        = useState("devices");
  const [devices,    setDevices]    = useState([]);
  const [bleEdit,    setBleEdit]    = useState(null); // 수정 대상 device 객체
  const [bleForm,    setBleForm]    = useState({});
  const [bleSaving,  setBleSaving]  = useState(false);
  const [bleMsg,     setBleMsg]     = useState("");

  useEffect(() => { fetchDevices(); }, []);

  const fetchDevices = () =>
    fetch(`${API_BASE}/api/ble/devices`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then(setDevices);

  const toggleDevice = async (id) => {
    await fetch(`${API_BASE}/api/ble/devices/${id}/toggle`, { method: "PATCH", headers: authHeaders(token) });
    fetchDevices();
  };

  const deleteDevice = async (id) => {
    if (!window.confirm(`삭제하면 해당 데이터도 삭제됩니다. 계속할까요?`)) return;
    await fetch(`${API_BASE}/api/ble/devices/${id}`, { method: "DELETE", headers: authHeaders(token) });
    fetchDevices();
  };

  const saveBleEdit = async () => {
    if (!bleEdit) return;
    setBleSaving(true); setBleMsg("");
    const res = await fetch(`${API_BASE}/api/ble/devices/${bleEdit.id}`, {
      method: "PATCH", headers: authHeaders(token),
      body: JSON.stringify({ ...bleForm, expires_at: bleForm.expires_at || null }),
    });
    setBleSaving(false);
    if (res.ok) { setBleMsg("✅ 저장 완료"); fetchDevices(); setTimeout(() => setBleEdit(null), 800); }
    else setBleMsg("❌ 저장 실패");
  };

  const tabVisible = (key) => !tabs || tabs.includes(key);

  return (
    <div style={{ padding: "15px", fontFamily: "sans-serif", backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        {tabVisible("ble_bt")       && <button style={S.tabBtn(tab === "devices")}   onClick={() => setTab("devices")}>📋 블투</button>}
        {tabVisible("ble_nfc")      && <button style={S.tabBtn(tab === "nfc")}       onClick={() => setTab("nfc")}>📱 NFC</button>}
        {tabVisible("ble_remote")   && <button style={S.tabBtn(tab === "remote")}    onClick={() => setTab("remote")}>🔗 원격</button>}
        {tabVisible("ble_log")      && <button style={S.tabBtn(tab === "logs")}      onClick={() => setTab("logs")}>🚪 로그</button>}
        {tabVisible("ble_settings") && <button style={S.tabBtn(tab === "settings")}  onClick={() => setTab("settings")}>⚙️ 설정</button>}
      </div>

      {tab === "devices"   && (
        <DevicesTab devices={devices} fetchDevices={fetchDevices}
          toggleDevice={toggleDevice} deleteDevice={deleteDevice}
          L={L} tenantId={tenantId} template={template}
          setBleEdit={(d) => { setBleEdit(d); setBleForm({ user_name: d.user_name, phone: d.phone, expires_at: d.expires_at?.slice(0,10)||"" }); setBleMsg(""); }} />
      )}
      {tab === "remote"    && <RemoteTab token={token} L={L} />}
      {tab === "logs"      && <LogsTab devices={devices} token={token} />}
      {tab === "nfc"       && <NfcTab token={token} tenantId={tenantId} template={template} />}
      {tab === "settings"  && <SettingsTab token={token} />}

      {bleEdit && (
        <BottomSheet onClose={() => setBleEdit(null)} title={`✏️ ${bleEdit.room_no}호 ${bleEdit.user_name} 수정`}>
          <MsgBox msg={bleMsg} />
          <label style={S.label}>이름</label>
          <input value={bleForm.user_name || ""} onChange={(e) => setBleForm((f) => ({ ...f, user_name: e.target.value }))} placeholder="이름" style={S.input} />
          <label style={S.label}>전화번호</label>
          <input value={bleForm.phone || ""} onChange={(e) => setBleForm((f) => ({ ...f, phone: e.target.value }))} placeholder="전화번호" type="tel" style={S.input} />
          <label style={S.label}>만료일 (비워두면 무기한)</label>
          <input value={bleForm.expires_at || ""} onChange={(e) => setBleForm((f) => ({ ...f, expires_at: e.target.value }))} type="date" style={{ ...S.input, marginBottom: "16px" }} />
          <div style={S.row}>
            <button onClick={() => setBleEdit(null)} style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "none", backgroundColor: "#f1f5f9", fontWeight: "bold", fontSize: "14px", cursor: "pointer" }}>취소</button>
            <button onClick={saveBleEdit} disabled={bleSaving} style={{ flex: 2, padding: "12px", borderRadius: "10px", border: "none", backgroundColor: bleSaving ? "#9ca3af" : "#4f46e5", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: bleSaving ? "not-allowed" : "pointer" }}>
              {bleSaving ? "저장 중..." : "✅ 저장"}
            </button>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}